import React, { useState, useCallback, useEffect } from 'react';
import { FestivalData, ImageData, NewsItem } from '../../../../model/FestivalData';
import ImageEditor from '../ImageEditor/ImageEditor';
import { fetchNews, updateNews, deleteNewsItem } from '../../../../services/api';
import { getImageUrl, ImageService } from '../../../../services/imageService';
import { formatDateWithoutMilliseconds } from '../../../../utils/dateUtils';
import { v4 as uuidv4 } from 'uuid';
import { FiChevronRight, FiImage, FiPlus } from 'react-icons/fi';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './NewsEditor.css';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface NewsEditorProps {
  sessionToken: string;
  festivalId: string;
  getApiImageUrl: (identifier: string) => string;
}

const NewsEditor: React.FC<NewsEditorProps> = ({ 
  sessionToken, 
  festivalId, 
  getApiImageUrl,
}) => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingIdentifier, setSavingIdentifier] = useState<string | null>(null);
  const [deletingIdentifier, setDeletingIdentifier] = useState<string | null>(null);
  const [unsavedNewsIds, setUnsavedNewsIds] = useState<string[]>([]);
  const [notifyUsersOnCreate, setNotifyUsersOnCreate] = useState(true);
  const t = useMessages('news');
  const common = useMessages('common');
  const { locale } = useLanguage();

  const imageService = new ImageService(sessionToken, festivalId);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const data = await loadTabData(festivalId, 'news', () => fetchNews(sessionToken, festivalId));
        setNewsItems(data);
      } catch (error) {
        console.error('Failed to load news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, [sessionToken, festivalId]);

  const newsTitlePlaceholder = t.titlePlaceholder;

  const handleAddNews = useCallback(() => {
    const newNewsItem: NewsItem = {
      identifier: uuidv4(),
      created_at: formatDateWithoutMilliseconds(new Date().toISOString()),
      title: newsTitlePlaceholder,
      image: null,
      summary: '',
      html_body: '',
    };
    
    setUnsavedNewsIds(prev => [...prev, newNewsItem.identifier]);
    setNewsItems(prevItems => [...prevItems, newNewsItem]);
    setEditingNews(newNewsItem);
  }, [newsTitlePlaceholder]);

  const handleSaveNews = useCallback(async (updatedNewsItem: NewsItem) => {
    try {
      setSavingIdentifier(updatedNewsItem.identifier);
      setSaveError(null);
      let newsToSave = { 
        ...updatedNewsItem,
        created_at: formatDateWithoutMilliseconds(updatedNewsItem.created_at)
      };
      
      if (newsToSave.image) {
        const uploadedImage = await imageService.uploadImageData(newsToSave.image);
        newsToSave = {
          ...newsToSave,
          image: uploadedImage
        };
      }

      // Determine if this is a new item (unsaved) or an update
      const isNewItem = unsavedNewsIds.includes(updatedNewsItem.identifier);
      const notifyUsers = isNewItem ? notifyUsersOnCreate : false;

      await updateNews(sessionToken, festivalId, newsToSave, notifyUsers);
      setNewsItems(prevItems => 
        prevItems.map(item => 
          item.identifier === newsToSave.identifier ? newsToSave : item
        )
      );
      setUnsavedNewsIds(prev => prev.filter(id => id !== newsToSave.identifier));
      setEditingNews(null);
      setNotifyUsersOnCreate(true); // Reset checkbox after saving
    } catch (error) {
      console.error('Failed to save news item:', error);
      setSaveError(error instanceof Error ? error.message : t.saveFailed);
    } finally {
      setSavingIdentifier(null);
    }
  }, [sessionToken, festivalId, imageService, unsavedNewsIds, notifyUsersOnCreate, t]);

  const handleRemoveNews = useCallback(async (identifier: string) => {
    try {
      setDeletingIdentifier(identifier);
      setSaveError(null);
      await deleteNewsItem(sessionToken, festivalId, identifier);
      const updatedNews = newsItems.filter(item => item.identifier !== identifier);
      setNewsItems(updatedNews);
      setEditingNews(null);
    } catch (error) {
      console.error('Failed to delete news item:', error);
      setSaveError(t.deleteFailed);
    } finally {
      setDeletingIdentifier(null);
    }
  }, [newsItems, sessionToken, festivalId, t]);

  const handleEditNews = useCallback((newsItem: NewsItem) => {
    setEditingNews(editingNews?.identifier === newsItem.identifier ? null : newsItem);
  }, [editingNews]);

  const handleNewsImageChange = useCallback((newImage: ImageData | undefined) => {
    if (editingNews) {
      const updatedNewsItem = { ...editingNews, image: newImage || null };
      setEditingNews(updatedNewsItem);
    }
  }, [editingNews]);

  const handleCancelEdit = useCallback((newsItem: NewsItem) => {
    setSaveError(null);
    if (unsavedNewsIds.includes(newsItem.identifier)) {
      setNewsItems(prevItems => 
        prevItems.filter(item => item.identifier !== newsItem.identifier)
      );
      setUnsavedNewsIds(prev => prev.filter(id => id !== newsItem.identifier));
    }
    setEditingNews(null);
  }, [unsavedNewsIds]);

  if (loading) {
    return (
      <section className="news-editor">
        <PageHeader title={t.title} />
        <LoadingPlaceholder layout="rows" />
      </section>
    );
  }

  const isUnsaved = editingNews !== null && unsavedNewsIds.includes(editingNews.identifier);

  return (
    <section className="news-editor">
      <PageHeader title={t.title} count={newsItems.length}>
        <button type="button" onClick={handleAddNews} className="btn btn-primary">
          <FiPlus aria-hidden="true" />
          {t.add}
        </button>
      </PageHeader>
      {newsItems.length === 0 ? (
        <div className="empty-state">{t.empty}</div>
      ) : (
        <ul className="news-list">
          {[...newsItems]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((item) => (
            <li key={item.identifier}>
              <button
                type="button"
                className="news-row"
                onClick={() => handleEditNews(item)}
              >
                {item.image ? (
                  <img src={getImageUrl(item.image, getApiImageUrl)} alt="" className="news-row-image" />
                ) : (
                  <span className="news-row-image">
                    <FiImage aria-hidden="true" />
                  </span>
                )}
                <span className="news-row-text">
                  <span className="news-row-title">{item.title}</span>
                  {item.summary && <span className="news-row-summary">{item.summary}</span>}
                </span>
                <span className="news-row-date">{new Date(item.created_at).toLocaleDateString(locale)}</span>
                <FiChevronRight className="news-row-chevron" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {editingNews && (
        <Modal
          key={editingNews.identifier}
          isOpen
          onClose={() => setEditingNews(null)}
          variant="sheet"
          className="news-sheet"
        >
          <h3>{isUnsaved ? t.newItem : t.editItem}</h3>
          {saveError && (
            <div className="news-sheet-error" role="alert">
              {saveError}
            </div>
          )}
          <div className="news-field">
            <label htmlFor="news-title">{t.titleLabel}</label>
            <input
              id="news-title"
              type="text"
              value={editingNews.title}
              onChange={(e) => setEditingNews({ ...editingNews, title: e.target.value })}
            />
          </div>
          <div className="news-field">
            <label htmlFor="news-summary">{t.summary}</label>
            <p id="news-summary-hint" className="news-field-hint">{t.summaryHint}</p>
            <textarea
              id="news-summary"
              aria-describedby="news-summary-hint"
              rows={3}
              value={editingNews.summary}
              onChange={(e) => setEditingNews({ ...editingNews, summary: e.target.value })}
            />
          </div>
          <div className="news-field">
            <label htmlFor="news-body">{t.body}</label>
            <p id="news-body-hint" className="news-field-hint">{t.bodyHint}</p>
            <textarea
              id="news-body"
              className="news-field-code"
              aria-describedby="news-body-hint"
              spellCheck={false}
              value={editingNews.html_body}
              onChange={(e) => setEditingNews({ ...editingNews, html_body: e.target.value })}
            />
          </div>
          <div className="news-field">
            <span className="news-field-label">{t.image}</span>
            <ImageEditor
              currentImage={editingNews.image || undefined}
              onChange={handleNewsImageChange}
              getApiImageUrl={getApiImageUrl}
            />
          </div>
          {isUnsaved && (
            <label className="news-notify">
              <input
                type="checkbox"
                checked={notifyUsersOnCreate}
                onChange={(e) => setNotifyUsersOnCreate(e.target.checked)}
              />
              {t.notifyUsers}
            </label>
          )}
          <div className="modal-actions">
            <button
              type="button"
              onClick={() => handleSaveNews(editingNews)}
              disabled={savingIdentifier === editingNews.identifier ||
                        (isUnsaved &&
                         (editingNews.title.trim() === newsTitlePlaceholder || editingNews.title.trim() === '' ||
                          editingNews.summary.trim() === ''))}
            >
              {savingIdentifier === editingNews.identifier ? t.saving : common.save}
            </button>
            <button type="button" onClick={() => handleCancelEdit(editingNews)}>{common.cancel}</button>
            {!isUnsaved && (
              <button
                type="button"
                className="danger"
                onClick={() => handleRemoveNews(editingNews.identifier)}
                disabled={deletingIdentifier === editingNews.identifier}
              >
                {deletingIdentifier === editingNews.identifier ? t.removing : t.remove}
              </button>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
};

export default NewsEditor;